import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Search, Trash2 } from 'lucide-react';
import { AI_NAMESPACES, type AiCacheItemView } from '@en3/shared';
import { adminApi } from '@/api/admin.api';
import { queryKeys } from '@/api/client';
import { toDisplayMessage } from '@/api/errors';
import { EmptyBlock, ErrorBlock, LoadingBlock, Pagination } from '@/components/common/States';
import { Button } from '@/components/ui/button';
import { Badge, Card, CardBody, CardHeader, CardTitle, Select } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

/**
 * AI 缓存列表（后台排查用）。
 *
 * 这个表回答的是"AI 通道刚才发生了什么"：哪些命名空间在跑、命中了多少次、
 * 有没有 error、失败样本的报错是什么。因此排序与筛选维度都围绕**排查**设计：
 * namespace（哪条链路）、status（是不是故障）、days（刚才还是上个月）。
 *
 * 三个实现取舍：
 *
 * 1. **筛选与分页状态都在本组件内部**。它是自成一体的"排查工具"，
 *    父页面（AdminPage）不该知道"用户正在看哪个命名空间"；把状态提上去只会让
 *    总览卡片跟着无关状态重渲染。
 *
 * 2. **请求/响应摘要正文截断、全文放 `title`**。摘要是给"扫一眼"的，
 *    但排查时常常需要看完整 prompt —— 用原生 `title` 提供全文比再做一个详情弹窗
 *    更省事，也不打断滚动。注意长度上限在后端已经截到 200 字（`requestPreview`），
 *    这里只负责显示层再收窄。
 *
 * 3. **移动端退化成卡片列表**（`md:` 以上才是 `<table>`）。这张表有 8 列，
 *    在 375px 上无论怎么压都会横向滚动；横向滚动的表格在手机上基本没法读，
 *    所以窄屏改成一列卡片、每个字段一行。表格容器自己带 `overflow-x-auto`，
 *    即使中等宽度下也不会把整页撑出横向滚动条。
 */

const PAGE_SIZE = 20;

const DAYS_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '', label: '全部时间' },
  { value: '1', label: '最近 1 天' },
  { value: '7', label: '最近 7 天' },
  { value: '30', label: '最近 30 天' },
  { value: '90', label: '最近 90 天' },
];

/**
 * 时间列：后台排查要能一眼看出"这条是刚才的还是上个月的"，所以用**绝对时间**，
 * 不写"3 分钟前"那种相对描述 —— 排查故障时对齐日志用的是钟表时间。
 */
function formatDateTime(epochMs: number): string {
  if (!Number.isFinite(epochMs) || epochMs <= 0) return '—';
  return new Date(epochMs).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** 显示层截断：`title` 里给全文，正文里只给开头。 */
function truncate(text: string | null, max = 100): string {
  if (text === null || text.length === 0) return '—';
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'ok') return <Badge tone="success">ok</Badge>;
  if (status === 'error') return <Badge tone="danger">error</Badge>;
  return <Badge tone="muted">{status}</Badge>;
}

/** 请求/响应摘要单元格（卡片与表格共用，避免两处截断逻辑漂移）。 */
function PreviewCell({ label, text }: { label: string; text: string | null }) {
  return (
    <div className="min-w-0">
      <span className="text-muted-foreground">{label}：</span>
      <span title={text ?? undefined} className="break-words">
        {truncate(text)}
      </span>
    </div>
  );
}

function ActionsCell({
  item,
  onDelete,
  className,
}: {
  item: AiCacheItemView;
  onDelete: (item: AiCacheItemView) => void;
  className?: string;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => onDelete(item)}
      aria-label={`删除缓存 ${item.id}`}
      className={className}
    >
      <Trash2 aria-hidden className="size-3.5" />
      删除
    </Button>
  );
}

export function AiCacheTable({ className }: { className?: string }) {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [namespace, setNamespace] = useState('');
  const [status, setStatus] = useState<'' | 'ok' | 'error'>('');
  const [days, setDays] = useState('');
  /** 输入框里的字（马上变化）与真正进请求的 `q`（防抖后）分开，避免每敲一个字打一次后台 */
  const [qText, setQText] = useState('');
  const [q, setQ] = useState('');
  const [confirming, setConfirming] = useState<AiCacheItemView | null>(null);

  useEffect(() => {
    const next = qText.trim();
    if (next === q) return;
    const timer = window.setTimeout(() => {
      setQ(next);
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [qText, q]);

  const params = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      namespace: namespace.length > 0 ? namespace : undefined,
      status: status === '' ? undefined : status,
      q: q.length > 0 ? q : undefined,
      days: days === '' ? undefined : Number(days),
    }),
    [page, namespace, status, q, days],
  );

  const list = useQuery({
    queryKey: queryKeys.adminAiCache(params),
    queryFn: () => adminApi.aiCache(params),
  });

  const remove = useMutation({
    mutationFn: (target: { id: string; lastOnPage: boolean }) => adminApi.deleteAiCache(target.id),
    onSuccess: async (_data, target) => {
      setConfirming(null);
      // 删掉本页最后一条时把页码往回退一格，避免停在空页上（原先是 effect 兜的，
      // 现在放在事件回调里：少一轮渲染，也不和渲染顺序较劲）。
      if (target.lastOnPage && page > 1) setPage(page - 1);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'ai-cache'] });
      // 总览里的"缓存条目/失败条目/命中次数"也变了，一并失效，否则两处数字互相矛盾
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminOverview });
    },
  });

  /**
   * namespace 选项：以 shared 的 `AI_NAMESPACES` 为骨架，计数取自后端的 `stats.byNamespace`。
   *
   * 为什么不是"纯用 stats.byNamespace 的键"：后端的统计与列表用**同一个 where**
   * （这样"当前条件下的事实"才自洽，不会出现"列表全是 error、统计却说 95% 成功"）。
   * 代价是筛过某个命名空间后，统计里就只剩那一个键 —— 只按统计生成下拉的话，
   * 下拉里会突然只剩一项，看起来像筛选器坏了。因此骨架用固定枚举，计数用统计，
   * 库里出现枚举外的命名空间（新功能上线时可能发生）也会被并进来。
   */
  const namespaceOptions = useMemo(() => {
    const counts = list.data?.stats.byNamespace ?? {};
    const known = AI_NAMESPACES.map((name) => [name, counts[name]] as const);
    const extra = Object.entries(counts)
      .filter(([name]) => !(AI_NAMESPACES as readonly string[]).includes(name))
      .sort((a, b) => b[1] - a[1]);
    return [...known, ...extra];
  }, [list.data]);

  const items = list.data?.items ?? [];
  const stats = list.data?.stats;
  const avgLatencyMs = stats?.avgLatencyMs ?? null;

  const hasFilters = namespace !== '' || status !== '' || q !== '' || days !== '';

  function resetFilters() {
    setNamespace('');
    setStatus('');
    setDays('');
    setQText('');
    setQ('');
    setPage(1);
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">AI 缓存与调用记录</CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void list.refetch()}
          loading={list.isFetching && !list.isPending}
          aria-label="刷新 AI 缓存列表"
        >
          <RefreshCw aria-hidden className="size-3.5" />
          刷新
        </Button>
      </CardHeader>

      <CardBody className="space-y-4">
        {/* 统计行：先说"当前条件下发生了什么"，再看具体记录。
            统计与列表用同一套筛选（后端保证），所以这里必须写明口径是"当前筛选" */}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge tone="success" className="px-2.5 py-1">
            成功 <span className="font-semibold tabular-nums">{stats?.okCount ?? 0}</span>
          </Badge>
          <Badge tone="danger" className="px-2.5 py-1">
            失败 <span className="font-semibold tabular-nums">{stats?.errorCount ?? 0}</span>
          </Badge>
          <Badge tone="primary" className="px-2.5 py-1">
            命中 <span className="font-semibold tabular-nums">{stats?.totalHits ?? 0}</span>
          </Badge>
          <Badge tone="muted" className="px-2.5 py-1">
            平均延迟{' '}
            <span className="font-semibold tabular-nums">
              {avgLatencyMs === null ? '—' : `${Math.round(avgLatencyMs)} ms`}
            </span>
          </Badge>
          {stats !== undefined && (
            <span className="text-muted-foreground">
              共 {list.data?.total ?? 0} 条
              {hasFilters ? '（当前筛选下）' : ''}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <label htmlFor="ai-cache-namespace" className="text-xs font-medium">
              命名空间
            </label>
            <Select
              id="ai-cache-namespace"
              value={namespace}
              className="w-full"
              onChange={(event) => {
                setNamespace(event.target.value);
                setPage(1);
              }}
            >
              <option value="">全部</option>
              {namespaceOptions.map(([name, total]) => (
                <option key={name} value={name}>
                  {name}
                  {typeof total === 'number' ? `（${total}）` : ''}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="ai-cache-status" className="text-xs font-medium">
              状态
            </label>
            <Select
              id="ai-cache-status"
              value={status}
              className="w-full"
              onChange={(event) => {
                setStatus(event.target.value as '' | 'ok' | 'error');
                setPage(1);
              }}
            >
              <option value="">全部</option>
              <option value="ok">成功（ok）</option>
              <option value="error">失败（error）</option>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="ai-cache-days" className="text-xs font-medium">
              时间范围
            </label>
            <Select
              id="ai-cache-days"
              value={days}
              className="w-full"
              onChange={(event) => {
                setDays(event.target.value);
                setPage(1);
              }}
            >
              {DAYS_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="ai-cache-q" className="text-xs font-medium">
              搜索（prompt / 响应 / 报错）
            </label>
            <div className="relative">
              <Search aria-hidden className="pointer-events-none absolute top-3 left-2.5 size-4 text-muted-foreground" />
              <Input
                id="ai-cache-q"
                type="search"
                value={qText}
                onChange={(event) => setQText(event.target.value)}
                placeholder="例如 timeout"
                className="pl-8"
              />
            </div>
          </div>
        </div>

        {remove.isError && (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            删除失败：{toDisplayMessage(remove.error)}
          </p>
        )}

        {list.isPending ? (
          <LoadingBlock label="正在加载 AI 缓存…" />
        ) : list.isError ? (
          <ErrorBlock message={toDisplayMessage(list.error)} onRetry={() => void list.refetch()} />
        ) : items.length === 0 ? (
          hasFilters ? (
            <EmptyBlock
              title="当前条件下没有记录"
              description="换个命名空间或时间范围试试。"
              action={
                <Button variant="outline" onClick={resetFilters}>
                  清空筛选
                </Button>
              }
            />
          ) : (
            <EmptyBlock title="还没有 AI 调用记录" description="有查词、长难句解析或建议生成后，这里会出现对应的缓存条目。" />
          )
        ) : (
          <>
            {/* 宽屏：表格 */}
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full border-collapse text-left text-xs">
                <caption className="sr-only">AI 缓存记录列表</caption>
                <thead className="text-muted-foreground">
                  <tr className="border-b">
                    <th scope="col" className="py-2 pr-3 font-medium">命名空间</th>
                    <th scope="col" className="py-2 pr-3 font-medium">状态</th>
                    <th scope="col" className="py-2 pr-3 text-right font-medium">命中</th>
                    <th scope="col" className="py-2 pr-3 text-right font-medium">延迟</th>
                    <th scope="col" className="py-2 pr-3 text-right font-medium">Tokens</th>
                    <th scope="col" className="py-2 pr-3 font-medium">时间</th>
                    <th scope="col" className="py-2 pr-3 font-medium">请求 / 响应摘要</th>
                    <th scope="col" className="py-2 text-right font-medium">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-b align-top last:border-b-0">
                      <td className="py-2 pr-3">
                        <span className="font-medium">{item.namespace}</span>
                        <div className="text-[11px] text-muted-foreground">
                          {item.provider}
                          {item.model ? ` · ${item.model}` : ''}
                          {item.promptVersion ? ` · ${item.promptVersion}` : ''}
                        </div>
                      </td>
                      <td className="py-2 pr-3">
                        <StatusBadge status={item.status} />
                        {item.errorMessage && (
                          <div title={item.errorMessage} className="mt-1 max-w-[12rem] break-words text-[11px] text-destructive">
                            {truncate(item.errorMessage, 60)}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{item.hitCount}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {item.latencyMs === null ? '—' : `${item.latencyMs} ms`}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {item.tokensIn === null && item.tokensOut === null
                          ? '—'
                          : `${item.tokensIn ?? 0} / ${item.tokensOut ?? 0}`}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap tabular-nums">{formatDateTime(item.createdAt)}</td>
                      <td className="max-w-[20rem] py-2 pr-3 text-[11px]">
                        <PreviewCell label="请求" text={item.requestPreview} />
                        <PreviewCell label="响应" text={item.responsePreview} />
                      </td>
                      <td className="py-2 text-right">
                        <ActionsCell item={item} onDelete={setConfirming} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 窄屏：卡片。8 列表格在手机上横向滚动没法读，这里每个字段一行 */}
            <ul className="space-y-3 md:hidden">
              {items.map((item) => (
                <li key={item.id} className="rounded-lg border p-3 text-xs">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{item.namespace}</span>
                    <StatusBadge status={item.status} />
                    <span className="ml-auto text-muted-foreground tabular-nums">{formatDateTime(item.createdAt)}</span>
                  </div>

                  <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
                    <div className="flex justify-between gap-2">
                      <dt className="text-muted-foreground">命中</dt>
                      <dd className="tabular-nums">{item.hitCount}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-muted-foreground">延迟</dt>
                      <dd className="tabular-nums">{item.latencyMs === null ? '—' : `${item.latencyMs} ms`}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-muted-foreground">Tokens</dt>
                      <dd className="tabular-nums">
                        {item.tokensIn === null && item.tokensOut === null ? '—' : `${item.tokensIn ?? 0}/${item.tokensOut ?? 0}`}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-muted-foreground">提供方</dt>
                      <dd className="truncate">{item.provider}</dd>
                    </div>
                  </dl>

                  <div className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                    <PreviewCell label="请求" text={item.requestPreview} />
                    <PreviewCell label="响应" text={item.responsePreview} />
                    {item.errorMessage && (
                      <div className="text-destructive">
                        <PreviewCell label="报错" text={item.errorMessage} />
                      </div>
                    )}
                  </div>

                  <div className="mt-2 flex justify-end">
                    <ActionsCell item={item} onDelete={setConfirming} />
                  </div>
                </li>
              ))}
            </ul>

            <Pagination page={page} totalPages={list.data?.totalPages ?? 0} onChange={setPage} />
          </>
        )}
      </CardBody>

      {/* 破坏性操作二次确认：把"删哪一条"写清楚，避免在长列表里误点 */}
      <Dialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title="删除这条 AI 缓存？"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirming(null)}>
              取消
            </Button>
            <Button
              variant="destructive"
              loading={remove.isPending}
              onClick={() => {
                if (confirming) remove.mutate({ id: confirming.id, lastOnPage: items.length <= 1 });
              }}
            >
              确认删除
            </Button>
          </>
        }
      >
        {confirming && (
          <div className="space-y-2 text-sm">
            <p>
              命名空间 <code className="rounded bg-muted px-1 py-0.5">{confirming.namespace}</code>、
              创建于 {formatDateTime(confirming.createdAt)}。
            </p>
            <p className="text-xs text-muted-foreground">
              删除后同一请求下次会重新调用 AI（会消耗额度），并尽量清掉对应的 KV 键。此操作不可撤销。
            </p>
            <p className="text-xs text-muted-foreground">记录 id：{confirming.id}</p>
          </div>
        )}
      </Dialog>
    </Card>
  );
}
